/**
 * Playo Booking Consolidation Utilities
 * 
 * Groups consecutive Playo bookings by playoOrderId to show them as
 * a single consolidated booking in the UI while maintaining database integrity.
 */

/**
 * Check if two time strings are consecutive (30 minutes apart)
 * @param {string} endTime - End time of first slot (HH:MM)
 * @param {string} startTime - Start time of second slot (HH:MM)
 * @returns {boolean}
 */
const areTimesConsecutive = (endTime, startTime) => {
  if (!endTime || !startTime) return false;
  
  const [endH, endM] = endTime.split(':').map(Number);
  const [startH, startM] = startTime.split(':').map(Number);
  
  // Handle midnight crossing
  let endMinutes = endH * 60 + endM;
  let startMinutes = startH * 60 + startM;
  
  // If start is much smaller than end, it might be next day
  if (startMinutes < endMinutes - 60) {
    startMinutes += 24 * 60;
  }
  
  return startMinutes === endMinutes;
};

/**
 * Sort bookings by start time
 */
const sortByStartTime = (a, b) => {
  const aTime = a.start_time || a.startTime || '';
  const bTime = b.start_time || b.startTime || '';
  return aTime.localeCompare(bTime);
};

/**
 * Group Playo bookings that belong to the same session
 * @param {Array} bookings - Array of bookings
 * @returns {Object} - { consolidated: Array, playoGroups: Object }
 */
export const consolidatePlayoBookings = (bookings) => {
  if (!bookings || !Array.isArray(bookings)) {
    return { consolidated: [], playoGroups: {} };
  }
  
  // Separate Playo and non-Playo bookings
  const playoBookings = bookings.filter(b => b.source === 'playo');
  const otherBookings = bookings.filter(b => b.source !== 'playo');
  
  // Group Playo bookings by playoOrderId, date, and court
  const playoGroupsMap = {};
  
  playoBookings.forEach(booking => {
    // Create a group key based on common attributes
    const date = booking.date;
    const courtId = booking.court_id || booking.section;
    const customerPhone = booking.customer_phone || booking.customerPhone;
    
    // Use playoOrderId prefix as group key (Playo orders have related IDs)
    // Format is typically: PLB-XXXXXXXX or similar
    const playoId = booking.playoOrderId || booking.id;
    
    // Group by date + court + customer + approximate time window
    // This catches bookings that are part of the same user session
    const groupKey = `${date}_${courtId}_${customerPhone}`;
    
    if (!playoGroupsMap[groupKey]) {
      playoGroupsMap[groupKey] = [];
    }
    playoGroupsMap[groupKey].push(booking);
  });
  
  // Now consolidate each group into consecutive slot chains
  const consolidatedPlayoBookings = [];
  const playoGroups = {};
  
  Object.entries(playoGroupsMap).forEach(([groupKey, groupBookings]) => {
    // Sort by start time
    const sorted = [...groupBookings].sort(sortByStartTime);
    
    // Find consecutive chains
    let currentChain = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const prevBooking = currentChain[currentChain.length - 1];
      const currBooking = sorted[i];
      
      const prevEnd = prevBooking.end_time || prevBooking.endTime;
      const currStart = currBooking.start_time || currBooking.startTime;
      
      if (areTimesConsecutive(prevEnd, currStart)) {
        // Consecutive - add to current chain
        currentChain.push(currBooking);
      } else {
        // Not consecutive - finalize current chain and start new one
        if (currentChain.length > 1) {
          const consolidated = createConsolidatedBooking(currentChain);
          consolidatedPlayoBookings.push(consolidated);
          playoGroups[consolidated.id] = currentChain;
        } else {
          consolidatedPlayoBookings.push(currentChain[0]);
        }
        currentChain = [currBooking];
      }
    }
    
    // Don't forget the last chain
    if (currentChain.length > 1) {
      const consolidated = createConsolidatedBooking(currentChain);
      consolidatedPlayoBookings.push(consolidated);
      playoGroups[consolidated.id] = currentChain;
    } else if (currentChain.length === 1) {
      consolidatedPlayoBookings.push(currentChain[0]);
    }
  });
  
  // Combine consolidated Playo bookings with other bookings
  const consolidated = [...otherBookings, ...consolidatedPlayoBookings];
  
  return { consolidated, playoGroups };
};

/**
 * Create a consolidated booking from multiple consecutive bookings
 * @param {Array} bookings - Array of consecutive bookings
 * @returns {Object} - Consolidated booking object
 */
const createConsolidatedBooking = (bookings) => {
  if (!bookings || bookings.length === 0) return null;
  if (bookings.length === 1) return bookings[0];
  
  // Sort to ensure correct order
  const sorted = [...bookings].sort(sortByStartTime);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  
  // Calculate totals
  const totalAmount = sorted.reduce((sum, b) => sum + (b.amount || 0), 0);
  const totalPaid = sorted.reduce((sum, b) => sum + (b.amount_paid || b.amountPaid || 0), 0);
  const totalPending = totalAmount - totalPaid;
  
  // Calculate duration in minutes
  const startTime = first.start_time || first.startTime;
  const endTime = last.end_time || last.endTime;
  const duration = calculateDurationMinutes(startTime, endTime);
  
  // Create consolidated booking
  return {
    // Use first booking's ID with a prefix to identify as consolidated
    id: `PLAYO_GROUP_${first.id}`,
    originalIds: sorted.map(b => b.id),
    isConsolidated: true,
    slotCount: sorted.length,
    
    // Use first booking's base info
    date: first.date,
    court_id: first.court_id || first.section,
    section: first.section,
    slot_type: first.slot_type || first.slotType,
    sport: first.sport,
    customer_name: first.customer_name || first.customerName,
    customerName: first.customer_name || first.customerName,
    customer_email: first.customer_email || first.customerEmail,
    customerEmail: first.customer_email || first.customerEmail,
    customer_phone: first.customer_phone || first.customerPhone,
    customerPhone: first.customer_phone || first.customerPhone,
    booking_type: first.booking_type || first.bookingType || 'b2c',
    bookingType: first.booking_type || first.bookingType || 'b2c',
    source: 'playo',
    status: first.status,
    facility_id: first.facility_id,
    
    // Consolidated time range
    start_time: startTime,
    startTime: startTime,
    end_time: endTime,
    endTime: endTime,
    duration: duration,
    
    // Aggregated amounts
    amount: totalAmount,
    amount_paid: totalPaid,
    amountPaid: totalPaid,
    amount_pending: totalPending,
    amountPending: totalPending,
    payment_status: totalPaid >= totalAmount ? 'paid' : totalPaid > 0 ? 'partial' : 'pending',
    paymentStatus: totalPaid >= totalAmount ? 'paid' : totalPaid > 0 ? 'partial' : 'pending',
    
    // Reference to original bookings for breakdown
    _originalBookings: sorted,
    
    // Created at from first booking
    created_at: first.created_at,
    created_by: first.created_by || 'Playo'
  };
};

/**
 * Calculate duration in minutes between two time strings
 */
const calculateDurationMinutes = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  
  let startMins = startH * 60 + startM;
  let endMins = endH * 60 + endM;
  
  // Handle overnight bookings
  if (endMins <= startMins) {
    endMins += 24 * 60;
  }
  
  return endMins - startMins;
};

/**
 * Format duration for display
 */
export const formatDuration = (minutes) => {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
};

/**
 * Check if a booking is a consolidated Playo booking
 */
export const isConsolidatedPlayoBooking = (booking) => {
  return booking?.isConsolidated === true && booking?.source === 'playo';
};

/**
 * Get the original bookings from a consolidated booking
 */
export const getOriginalBookings = (consolidatedBooking) => {
  return consolidatedBooking?._originalBookings || [consolidatedBooking];
};

export default {
  consolidatePlayoBookings,
  formatDuration,
  isConsolidatedPlayoBooking,
  getOriginalBookings
};
